// client/src/coms/settings/sections/ShortcutSettings.js
// Personal Settings → Keyboard Shortcuts: editable mute hotkeys, and the fixed shortcuts for tabs, split view,
// the server list and resizing panels.
import React from 'react';
import { Row, Section } from '../../personalisation/settingsControls';
import { SHORTCUTS } from '../../personalisation/PersonalisationSettings';
import { MUTE_HOTKEYS } from '../muteHotkeys';
import { KeyCap } from './VoiceVideoSettings';

const OTHER_SHORTCUTS = [
  ['Alt+Shift+↑ / ↓', 'Move the selected server up or down (server sidebar)'],
  ['← / → on a panel divider', 'Resize the channel or member list'],
  ['Esc', 'Close the open dialog or menu'],
];

export default function ShortcutSettings({ keys }) {
  const anyChanged = Object.keys(MUTE_HOTKEYS).some((k) => keys.hotkeys[k] !== MUTE_HOTKEYS[k].fallback);
  return (
    <Section
      id="vx-cat-shortcuts"
      title="Keyboard Shortcuts"
      description={
        keys.inTauri
          ? 'Mute hotkeys work everywhere, also while the desktop app is in the background.'
          : 'Mute hotkeys work while Voxemera is the active tab and you are in a voice channel. The desktop app can also catch them in the background.'
      }
      actions={
        anyChanged ? (
          <button type="button" className="vx-btn small" onClick={keys.resetAllHotkeys} data-testid="hotkeys-reset-all">
            Restore default hotkeys
          </button>
        ) : null
      }
    >
      <div id="vx-setting-mute-hotkeys" data-setting="mute-hotkeys" />
      {Object.entries(MUTE_HOTKEYS).map(([kind, def]) => {
        const value = keys.hotkeys[kind];
        const capturing = keys.capturing === kind;
        return (
          <Row key={kind} label={def.label} scope="device" onReset={() => keys.resetHotkey(kind)} isDefault={value === def.fallback} resetLabel={`Reset to ${def.fallback}`}>
            <div className="vx-inline" data-testid={`hotkey-${kind}`}>
              <KeyCap muted={!value}>{capturing ? 'Press keys…' : value || 'Not set'}</KeyCap>
              <button type="button" className={`vx-btn small${capturing ? ' is-on' : ''}`} aria-pressed={capturing} onClick={() => keys.startCapture(kind)}>
                {capturing ? 'Cancel' : value ? 'Change' : 'Set'}
              </button>
              {value ? (
                <button type="button" className="vx-link-btn" onClick={() => keys.clearHotkey(kind)}>
                  Turn off
                </button>
              ) : null}
            </div>
            {keys.problem.kind === kind && keys.problem.text ? (
              <div className="vx-acct-error" role="alert">
                {keys.problem.text}
              </div>
            ) : null}
          </Row>
        );
      })}
      {keys.capturing && keys.capturing !== 'ptt' ? (
        <div className="vx-set-note" role="status">
          Press the new shortcut, including Ctrl, Alt or Shift. Esc cancels.
        </div>
      ) : null}

      <h4 className="vx-set-subtitle" id="vx-setting-tab-shortcuts" data-setting="tab-shortcuts">
        Tabs, split view and panels
      </h4>
      <table className="vx-shortcuts">
        <tbody>
          {[...SHORTCUTS, ...OTHER_SHORTCUTS].map(([k, d]) => (
            <tr key={k}>
              <th scope="row">
                <kbd>{k}</kbd>
              </th>
              <td>{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="vx-set-hint">These are fixed. On a Mac, use Option for Alt.</div>
    </Section>
  );
}
